import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Users,
  ShieldCheck,
  HardDrive,
  FileText,
  Globe,
  Key,
  Settings,
  Plus,
  Info,
  Save,
  X,
  Edit3,
  Search,
  Filter,
  RefreshCw,
  Loader,
  UserX,
  UserCheck,
  Send,
  Trash2,
  Eye,
  Lock,
  Copy,
  ShieldAlert,
  Database,
  Zap,
  Activity,
  Server,
  Wifi,
  Cpu,
  Monitor,
  Power,
  PowerOff,
  RotateCcw,
  Upload,
  Download,
  Stethoscope,
  ClipboardCheck,
  Briefcase,
  ExternalLink,
  Shield,
  Smartphone,
  CreditCard,
} from 'lucide-react';
import { Tag, message, Spin, Tooltip } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import { validatePasswordStrengthI18n } from '../../../helpers/i18n/passwordValidationI18n';
import { generateTemporaryPassword } from '../../../helpers/generatePassword';
import moment from 'moment';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';

import { Card } from '../../ComponnentsUI/common/Card';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { Select } from '../../ComponnentsUI/common/Select';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { ConfirmDialog } from '../../ComponnentsUI/common/ConfirmDialog';
import { Switch } from '../../ComponnentsUI/common/Switch';
import { Table } from '../../ComponnentsUI/common/Table';
import { Badge } from '../../ComponnentsUI/common/Badge';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../ComponnentsUI/common/ActionMenu';
import UserRolesModal from './UserRolesModal';
import OrgStructurePage from '../OrgStructure/org_structure.page';
import RolesPermissionsPage from '../RolesPermissions/roles_permissions.page';
import { useRoles } from '../../../helpers/api/roles/roles';
import { usePermissions } from '../../../helpers/acl/permissions';
import { useOrgTree, OrgUnitNode } from '../../../helpers/api/org-units/org-units';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

import {
  useOrganization,
  useOrganizationUpdate,
} from '../../../helpers/api/organization/organization';
import {
  useMe,
  usePaginatedUsers,
  useCreateUser,
  useImportUsersCsv,
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
  useResendVerification,
  useAdminSendPasswordReset,
  useAdress,
} from '../../../helpers/api/users/users';
import {
  useGetDevicesWithConnections,
  useStartDevice,
  useStopDevice,
  useRestartDevice,
  useDeleteDevice,
  useGetApplicationEntitiesPageable,
} from '../../../helpers/api/pacs/pacs';
import AEListing from '../../Components/Devices/ae-listing';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiLoader, FiRefreshCw } from 'react-icons/fi';
import {
  useSamlSettings,
  useUpdateSamlSettings,
} from '../../../helpers/api/auth/auth';
import Servers from '../../Components/Servers';
import { MobileLicensingTab } from './mobile-licensing-tab';

const API =
  (window as any).RUNTIME_ENV?.BACKEND_URL ??
  process.env.BACKEND_URL ??
  'http://localhost:8000/api/v1';

type BillingStatus = {
  subscription_status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  license_status: string;
  // "paid" | "grant". Grant tenants are DeepEcho-sponsored and have no self-serve billing.
  billing_type?: string;
};

async function billingRequest(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const response = await fetch(`${API}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const err = new Error(await response.text()) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return response.json();
}

type BillingTabProps = {
  status: BillingStatus | null;
  isLoading: boolean;
  // Error from loading the status (owned by the parent). Action errors are tracked locally.
  loadError: string;
  reload: () => Promise<void>;
};

const BillingTab = ({ status, isLoading, loadError, reload }: BillingTabProps) => {
  const [isOpening, setIsOpening] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const error = actionError || loadError;

  async function openBilling(kind: 'portal' | 'checkout') {
    try {
      setIsOpening(true);
      setNotice('');
      const result = await billingRequest(`/billing/${kind}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      window.open(result.url, '_blank', 'noopener,noreferrer');
      setActionError('');
    } catch (e) {
      const err_status = (e as { status?: number })?.status;
      if (kind === 'checkout' && err_status === 409) {
        // Already subscribed (a race/retry). Not an error - refresh so the button becomes
        // "Manage billing" and tell the user gently.
        setActionError('');
        setNotice('You already have an active subscription. Use Manage billing to view or update it.');
        await reload();
      } else {
        setActionError('Could not open Stripe billing. Please try again.');
      }
    } finally {
      setIsOpening(false);
    }
  }

  const subscriptionStatus = status?.subscription_status || 'unknown';
  const licenseStatus = status?.license_status || 'unknown';
  const needsAttention = ['past_due', 'suspended', 'canceled', 'unpaid'].includes(subscriptionStatus) || ['suspended', 'expired', 'revoked'].includes(licenseStatus);
  const hasNoSubscription = subscriptionStatus === 'none';
  const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const isUnknown = subscriptionStatus === 'unknown';

  return (
    <div className="space-y-6">
      <section className={`rounded-2xl border shadow-sm overflow-hidden ${needsAttention ? 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/20' : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-slate-900'}`}>
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className={needsAttention ? 'text-amber-600' : 'text-blue-600'} />
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight">Organization billing</h2>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : needsAttention ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300'}`}>
            {subscriptionStatus}
          </span>
        </div>
        <div className="p-6 space-y-4">
          {isLoading ? (
            <p className="text-sm font-bold text-gray-400 animate-pulse">Loading billing status...</p>
          ) : (
            <>
              <div>
                <p className="text-sm font-extrabold text-slate-800 dark:text-white">
                  {isUnknown
                    ? 'Billing status unavailable'
                    : needsAttention
                    ? 'Your subscription needs attention'
                    : hasNoSubscription
                    ? 'Subscribe to activate billing'
                    : isActive
                    ? 'Subscription active'
                    : 'Billing status unavailable'}
                </p>
                <p className="mt-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {isUnknown
                    ? "We couldn't reach billing right now. Refresh to try again."
                    : (
                      <>
                        License: {licenseStatus}.{' '}
                        {status?.current_period_end ? `Renews on ${new Date(status.current_period_end).toLocaleDateString()}.` : ''}
                        {status?.cancel_at_period_end ? ' Cancellation is scheduled at period end.' : ''}
                      </>
                    )}
                </p>
              </div>
              {!isUnknown ? (
                <button
                  type="button"
                  disabled={isOpening}
                  onClick={() => void openBilling(hasNoSubscription ? 'checkout' : 'portal')}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-black uppercase text-white transition disabled:opacity-60 ${needsAttention ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  <ExternalLink size={14} />
                  {hasNoSubscription ? 'Subscribe' : needsAttention ? 'Pay / Manage billing' : 'Manage billing'}
                </button>
              ) : null}
            </>
          )}
          {error ? <p className="text-xs font-bold text-red-600 dark:text-red-400">{error}</p> : null}
          {notice ? <p className="text-xs font-bold text-blue-600 dark:text-blue-300">{notice}</p> : null}
        </div>
      </section>
    </div>
  );
};

/**
 * Single source of truth for the tenant's billing status.
 *
 * Owned by the page (not BillingTab) so we fetch /billing/status exactly once and feed both the
 * tab-visibility decision and the tab's contents from it. Only fetches when `enabled` is true, so
 * users who can't see the Billing tab never make the call.
 *
 * Grant tenants are sponsored by DeepEcho: there is no subscription to manage, so the Billing tab
 * is hidden for them (see billingEnabled). Fails open - if the status call fails we keep billing
 * visible, because hiding it from a paying customer is the worse error.
 */
function useBillingStatus(enabled: boolean) {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setStatus(await billingRequest('/billing/status'));
      setError('');
    } catch {
      setError('Billing status is temporarily unavailable.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void loadStatus();
    window.addEventListener('focus', loadStatus);
    return () => window.removeEventListener('focus', loadStatus);
  }, [enabled, loadStatus]);

  return { status, isLoading, error, loadStatus };
}

const OrganizationPage: React.FC = () => {
  const { t } = useTranslation(['Organization', 'UserManagement', 'Common']);
  useDocumentTitle(`DEEPECHO - ${t('Organization')}`);
  // Reopen the section the user was last working in.
  const [activeTab, setActiveTabState] = useState(
    () => localStorage.getItem('org-settings:lastSection') || 'clinical'
  );
  const setActiveTab = (key: string) => {
    setActiveTabState(key);
    localStorage.setItem('org-settings:lastSection', key);
  };
  const location = useLocation();

  const extractSubdomain = (hostname: string) => {
    let effectiveHostname = hostname;
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    ) {
      effectiveHostname = 'dev.deepecho.io';
    }

    const parts = effectiveHostname.split('.');
    if (parts.length > 2) {
      return parts[0];
    }
    return '';
  };

  const {
    data: orgData,
    isLoading: orgLoading,
    refetch: refetchOrg,
  } = useOrganization();
  const { data: userData } = useMe();
  const isSuperAdmin = Boolean(userData?.is_super_admin);
  // Permission gating (UI only; backend enforces). Tab visibility uses the read
  // perm; each tab's edit/manage controls use the matching manage perm.
  const { can } = usePermissions();
  const canStaffRead = can('user.read');
  const canStaffManage = can('user.manage');
  const canOrgStructure = can('org_unit.read');
  const canRoles = can('role.read');
  const canSettings = can('org_unit.manage');
  const canPacsManage = can('pacs_device.manage');
  // Grant (DeepEcho-sponsored) tenants have no self-serve billing, so the tab is hidden. Only
  // fetch when the user can reach settings; fail open so a failed call keeps billing visible.
  const {
    status: billingStatus,
    isLoading: billingLoading,
    error: billingError,
    loadStatus: reloadBilling,
  } = useBillingStatus(canSettings);
  const billingEnabled = billingStatus?.billing_type !== 'grant';
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('pacsTab');
    if (!tab) return;
    const allowedTabs = new Set<string>();
    if (canOrgStructure) {
      allowedTabs.add('clinical');
      allowedTabs.add('org-structure');
    }
    if (canStaffRead) allowedTabs.add('staff');
    if (canRoles) allowedTabs.add('roles-permissions');
    if (canSettings) allowedTabs.add('security');
    if (canSettings) allowedTabs.add('billing');
    if (canPacsManage) allowedTabs.add('licensing');
    if (canPacsManage) {
      allowedTabs.add('pacs');
      allowedTabs.add('servers');
    }
    if (allowedTabs.has(tab)) {
      setActiveTab(tab);
    }
  }, [location.search, canOrgStructure, canStaffRead, canRoles, canSettings, canPacsManage]);

  const subdomain = useMemo(() => {
    if (typeof window !== 'undefined') {
      return extractSubdomain(window.location.hostname);
    }
    return '';
  }, []);

  const { data: address, isLoading, error } = useAdress(subdomain);

  if (orgLoading)
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 transition-colors">
        <Activity className="animate-spin text-blue-600" size={48} />
      </div>
    );
  return (
    <div className="w-full mx-auto space-y-6 bg-card-color-200 p-6 dark:bg-gray-900 transition-colors duration-300">
      {/* Page header — same pattern as the other pages (icon tile + title + subtitle, actions right) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-in fade-in">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg overflow-hidden">
            {orgData?.logo_url ? (
              <img
                src={orgData.logo_url}
                className="h-6 w-6 rounded object-cover"
                alt={t('header.logoAlt')}
              />
            ) : (
              <Building2 size={24} className="text-blue-600 dark:text-blue-400" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
              {orgData?.name || t('header.newFacility')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('header.subtitle', 'Organization settings & administration')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              refetchOrg();
              messageI18n.success('organization.instanceConfigSynced');
            }}
          >
            <RefreshCw size={14} className="mr-2" /> {t('header.syncConfig')}
          </Button>
        </div>
      </div>

      <OrgTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        items={[
          canOrgStructure && {
            key: 'clinical',
            label: t('tabs.profile'),
            icon: Stethoscope,
            content: (
              <ClinicalProfileTab
                orgData={orgData}
                isAdmin={canSettings}
                refetch={refetchOrg}
              />
            ),
          },
          canStaffRead && {
            key: 'staff',
            label: t('tabs.medicalStaff'),
            icon: Users,
            content: <StaffManagementTab isAdmin={canStaffManage} />,
          },
          canOrgStructure && {
            key: 'org-structure',
            label: t('tabs.orgStructure', 'Org Structure'),
            icon: Building2,
            content: <OrgStructurePage embedded />,
          },
          canRoles && {
            key: 'roles-permissions',
            label: t('tabs.rolesPermissions', 'Roles & Permissions'),
            icon: Lock,
            content: <RolesPermissionsPage embedded />,
          },
          canPacsManage && {
            key: 'licensing',
            label: t('tabs.mobileLicensing'),
            icon: Smartphone,
            content: <MobileLicensingTab />,
          },
          canSettings && billingEnabled && {
            key: 'billing',
            label: 'Billing',
            icon: CreditCard,
            content: (
              <BillingTab
                status={billingStatus}
                isLoading={billingLoading}
                loadError={billingError}
                reload={reloadBilling}
              />
            ),
          },
          canPacsManage && {
            key: 'pacs',
            label: t('tabs.pacsConnectivity'),
            icon: HardDrive,
            content: <AEListing user={userData} address={address} />,
          },
          canPacsManage && {
            key: 'servers',
            label: t('tabs.dicomServers'),
            icon: Server,
            content: <Servers />,
          },
          canSettings && {
            key: 'security',
            label: t('tabs.securitySso'),
            icon: ShieldCheck,
            content: (
              <SecurityTab
                orgData={orgData}
                isAdmin={canSettings}
                refetch={refetchOrg}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

// ---- Tab bar for the Organization page --------------------------------------

interface OrgTabItem {
  key: string;
  label: string;
  icon: React.ElementType;
  content: React.ReactNode;
}

/**
 * Horizontal segmented tab bar: pill buttons inside a card container,
 * horizontally scrollable when space is tight. Only the active tab's
 * content is rendered.
 */
const OrgTabs = ({
  items,
  activeTab,
  onChange,
}: {
  items: (OrgTabItem | false)[];
  activeTab: string;
  onChange: (key: string) => void;
}) => {
  const visible = items.filter(Boolean) as OrgTabItem[];
  // If the requested tab isn't visible (permissions), fall back to the first one.
  const active = visible.find(i => i.key === activeTab) ?? visible[0];
  if (!active) return null;

  return (
    <div className="space-y-6">
      <nav
        aria-label="Organization sections"
        className="rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="flex gap-1 overflow-x-auto">
          {visible.map(item => {
            const isActive = item.key === active.key;
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onChange(item.key)}
                aria-current={isActive ? 'page' : undefined}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200'
                }`}
              >
                <Icon size={16} className={isActive ? 'text-white' : 'text-slate-400 dark:text-gray-500'} />
                {item.label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="min-w-0 animate-in fade-in" key={active.key}>
        {active.content}
      </div>
    </div>
  );
};

const ClinicalFormField = ({
  label,
  children,
  error,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
}) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
      {label}
    </label>
    {children}
    {error && (
      <p className="text-[10px] text-red-500 font-medium mt-1">{error}</p>
    )}
  </div>
);

const ClinicalProfileTab = ({ orgData, isAdmin, refetch }: any) => {
  const { t } = useTranslation('Organization');
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (orgData && !isEditing) {
      setForm({
        ...orgData,
        name: orgData.name || '',
        contact_person_name: orgData.contact_person_name || '',
        facility_type: orgData.facility_type || 'Tertiary Maternity Care',
        address: orgData.address || '',
        email: orgData.email || '',
        phone: orgData.phone || '',
        logo_url: orgData.logo_url || null,
      });
    }
  }, [orgData, isEditing]);

  const updateOrg = useOrganizationUpdate(
    () => {
      messageI18n.success('organization.clinicalRecordsUpdated');
      setIsEditing(false);
      setFieldErrors({}); // Clear errors on success
      refetch();
    },
    (error: any) => {
      // Handle FastAPI/Pydantic validation errors
      console.error('Update error:', error);
      if (error?.detail) {
        const newErrors: Record<string, string> = {};
        error.detail.forEach((err: any) => {
          const fieldName = err.loc[err.loc.length - 1];
          newErrors[fieldName] = err.msg;
        });
        setFieldErrors(newErrors);
        messageI18n.error('organization.correctHighlightedFields');
      } else {
        messageI18n.error('organization.updateFailed');
      }
    }
  );
  const handleInputChange = (field: string, value: any) => {
    setForm({ ...form, [field]: value });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        messageI18n.error('organization.logoTooLarge');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setForm((prev) => ({ ...prev, logo_url: base64String }));
        messageI18n.info('organization.logoStaged');
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeLogo = () => {
    setForm((prev) => ({ ...prev, logo_url: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      contact_person_name: form.contact_person_name,
      facility_type: form.facility_type,
      address: form.address,
      email: form.email,
      phone: form.phone,
      logo_url: form.logo_url,
    };
    updateOrg.mutate(payload);
  };

  return (
    <div className="w-full animate-in slide-in-from-bottom-4 duration-500">
      <div className="lg:col-span-2 space-y-6">
        <Card
          title={t('clinical.medicalRecord')}
          controls={
            isAdmin && (
              <div className="flex gap-2">
                {isEditing && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      setForm(orgData);
                    }}
                  >
                    <X size={16} className="mr-2" /> {t('Cancel')}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={isEditing ? 'success' : 'primary'}
                  onClick={() =>
                    isEditing ? handleSave() : setIsEditing(true)
                  }
                >
                  {isEditing ? (
                    <Save size={16} className="mr-2" />
                  ) : (
                    <Edit3 size={16} className="mr-2" />
                  )}
                  {isEditing ? t('clinical.saveChanges') : t('clinical.editInfo')}
                </Button>
              </div>
            )
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ClinicalFormField label={t('clinical.facilityName')} error={fieldErrors.name}>
              <Input
                value={form.name}
                disabled={!isEditing}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`${fieldErrors.name ? 'border-red-500 focus:ring-red-500' : ''} dark:bg-gray-900`}
              />
            </ClinicalFormField>

            <ClinicalFormField
              label={t('clinical.medicalDirector')}
              error={fieldErrors.contact_person_name}
            >
              <Input
                value={form.contact_person_name}
                disabled={!isEditing}
                placeholder={isEditing ? undefined : t('clinical.notProvided', 'Not provided')}
                onChange={(e) =>
                  handleInputChange('contact_person_name', e.target.value)
                }
                className={`${fieldErrors.contact_person_name ? 'border-red-500' : ''} dark:bg-gray-900`}
              />
            </ClinicalFormField>
            <ClinicalFormField
              label={t('clinical.facilityType')}
              error={fieldErrors.facility_type}
            >
              <Select
                value={form.facility_type}
                disabled={!isEditing}
                onChange={(e) =>
                  handleInputChange('facility_type', e.target.value)
                }
                className={`w-full rounded-lg border p-2.5 bg-transparent text-sm outline-none focus:ring-2 transition-colors
                                    ${
                                      fieldErrors.facility_type
                                        ? 'border-red-500 focus:ring-red-500 text-red-600'
                                        : 'border-slate-200 dark:border-gray-700 dark:text-white focus:ring-blue-500'
                                    } 
                                    dark:bg-gray-900`}
                options={[
                  {
                    value: 'Tertiary Maternity Care',
                    label: t('facilityTypes.tertiaryMaternity'),
                  },
                  {
                    value: 'Private Fetal Clinic',
                    label: t('facilityTypes.privateFetal'),
                  },
                  {
                    value: 'Diagnostic Imaging Center',
                    label: t('facilityTypes.diagnosticImaging'),
                  },
                ]}
              />
            </ClinicalFormField>
            <ClinicalFormField
              label={t('clinical.medicalAddress')}
              error={fieldErrors.address}
            >
              <Input
                value={form.address}
                disabled={!isEditing}
                placeholder={isEditing ? undefined : t('clinical.notProvided', 'Not provided')}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`${fieldErrors.address ? 'border-red-500' : ''} dark:bg-gray-900`}
              />
            </ClinicalFormField>

            <ClinicalFormField label={t('clinical.clinicalEmail')} error={fieldErrors.email}>
              <Input
                value={form.email}
                disabled={!isEditing}
                placeholder={isEditing ? undefined : t('clinical.notProvided', 'Not provided')}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`${fieldErrors.email ? 'border-red-500' : ''} dark:bg-gray-900`}
              />
            </ClinicalFormField>

            <ClinicalFormField
              label={t('clinical.emergencyPhone')}
              error={fieldErrors.phone}
            >
              <Input
                value={form.phone}
                disabled={!isEditing}
                placeholder={isEditing ? undefined : t('clinical.notProvided', 'Not provided')}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`${fieldErrors.phone ? 'border-red-500' : ''} dark:bg-gray-900`}
              />
            </ClinicalFormField>
          </div>
        </Card>

        <Card title={t('clinical.brandingTitle')}>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div
              className={`w-40 h-40 border-2 border-dashed border-slate-300 dark:border-gray-600 rounded-2xl flex items-center justify-center bg-white dark:bg-gray-800 overflow-hidden shadow-sm transition-colors ${isEditing ? 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500' : ''}`}
              onClick={() => isEditing && fileInputRef.current?.click()}
            >
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  className="w-full h-full object-contain p-2"
                  alt={t('clinical.logoPreviewAlt')}
                />
              ) : (
                <Building2
                  className="text-slate-300 dark:text-slate-600"
                  size={48}
                />
              )}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
            </div>
            <div className="flex-1 space-y-4">
              <h4 className="font-bold text-slate-900 dark:text-white">
                {t('clinical.reportLogoTitle')}
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
                {t('clinical.reportLogoDescription')}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!isEditing}
                >
                  <Upload size={14} className="mr-2" /> {t('clinical.changeImage')}
                </Button>
                {form.logo_url && isEditing && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={removeLogo}
                  >
                    {t('clinical.removeImage')}
                  </Button>
                )}
                {!isEditing && isAdmin && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {t('clinical.editToChangeLogo', 'Click "Edit info" above to change the logo.')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const StaffManagementTab = ({ isAdmin }: any) => {
  const { t } = useTranslation(['Organization', 'UserManagement']);
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [rolesUser, setRolesUser] = useState<any>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filters, setFilters] = useState({ first_name: '', email: '' });
  const importInputRef = useRef<HTMLInputElement>(null);

  // Inputs are controlled for instant feedback; the actual filter (and refetch)
  // is debounced so we don't hit the API on every keystroke. Filter changes
  // jump back to the first page.
  const [filterInputs, setFilterInputs] = useState({ first_name: '', email: '' });
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateFilter = (key: 'first_name' | 'email', value: string) => {
    setFilterInputs(f => ({ ...f, [key]: value }));
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      setFilters(f => ({ ...f, [key]: value }));
      setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
    }, 350);
  };
  const hasActiveFilters = Boolean(filterInputs.first_name || filterInputs.email);
  const clearFilters = () => {
    if (filterTimer.current) clearTimeout(filterTimer.current);
    setFilterInputs({ first_name: '', email: '' });
    setFilters({ first_name: '', email: '' });
    setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  };
  useEffect(() => () => {
    if (filterTimer.current) clearTimeout(filterTimer.current);
  }, []);

  const createUser = useCreateUser();
  // Two independent choices: how the credentials are delivered, and whether they are a
  // temporary password or a setup link. A temporary password goes out over the same two
  // channels an invite does.
  const [createChannel, setCreateChannel] = useState<'email' | 'whatsapp'>('email');
  // A temporary password is the default: it works the same whether the person is
  // reachable by email or not, and the setup link is the exception now.
  const [sendSetupLink, setSendSetupLink] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  // Clear the credential when the dialog closes, so the next staff member added does
  // not open on the previous one's password still sitting in the field.
  useEffect(() => {
    if (!isCreateModalOpen) {
      setSendSetupLink(false);
      setTemporaryPassword('');
    }
  }, [isCreateModalOpen]);
  const { data: aclRoles } = useRoles();
  const { data: orgTree } = useOrgTree();
  // Restrict placement options to the selected role's assignable org levels.
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const orgNodes: { value: string; label: string; type: string }[] = [];
  const _walkOrg = (n: OrgUnitNode | null, depth: number) => {
    if (!n) return;
    orgNodes.push({ value: n.id, label: `${'- '.repeat(depth)}${n.name}`, type: n.type });
    (n.children || []).forEach(c => _walkOrg(c, depth + 1));
  };
  _walkOrg(orgTree ?? null, 0);
  const selectedRole = (aclRoles || []).find(r => r.id === selectedRoleId);
  const allowedLevels = selectedRole?.assignable_levels ?? null;
  const orgNodeOptions = (
    allowedLevels ? orgNodes.filter(n => allowedLevels.includes(n.type)) : orgNodes
  ).map(({ value, label }) => ({ value, label }));
  const importUsersCsv = useImportUsersCsv();
  const deactivateUser = useDeactivateUser();
  const activateUser = useActivateUser();
  const resendVerification = useResendVerification();
  const sendPasswordReset = useAdminSendPasswordReset();
  const deleteUser = useDeleteUser();

  // Confirmation step for destructive row actions (remove / suspend a user).
  const [confirmAction, setConfirmAction] = useState<
    { type: 'remove' | 'suspend'; user: any } | null
  >(null);
  const runConfirmAction = () => {
    if (!confirmAction) return;
    const mut = confirmAction.type === 'remove' ? deleteUser : deactivateUser;
    mut.mutate(confirmAction.user.id, {
      onSuccess: () => {
        setConfirmAction(null);
        users.refetch();
      },
    });
  };

  // "Send via" channel choice for admin link-sends (resend verification / reset password).
  const [channelChoice, setChannelChoice] = useState<
    { action: 'reset' | 'resend'; userId: string; phone?: string } | null
  >(null);
  const runChannelChoice = (channel: 'email' | 'whatsapp') => {
    if (!channelChoice) return;
    const { action, userId } = channelChoice;
    const mut = action === 'reset' ? sendPasswordReset : resendVerification;
    const okKey =
      action === 'reset' ? 'organization.passwordResetSent' : 'organization.verificationResent';
    const errKey =
      action === 'reset' ? 'organization.passwordResetFailed' : 'organization.verificationResendFailed';
    mut.mutate(
      { userId, channel },
      {
        onSuccess: () => {
          messageI18n.success(okKey);
          setChannelChoice(null);
        },
        onError: () => messageI18n.error(errKey),
      }
    );
  };

  const buildFilters = (filters) => {
    const nonEmptyFilters = Object.entries(filters)
      .filter(([, value]) => value)
      .reduce((acc, [key, value]) => {
        acc[`${key}__ilike`] = `%${value}%`;
        return acc;
      }, {});
    if (Object.keys(nonEmptyFilters).length === 0) return {};
    return { condition__or: nonEmptyFilters };
  };

  const users = usePaginatedUsers(
    pagination.pageIndex + 1,
    pagination.pageSize,
    {
      ...(Object.keys(filters).length > 0 && {
        filters: JSON.stringify(buildFilters(filters)),
      }),
    },
    { refetchInterval: 60000 }
  );

  const getStaffRoleLabel = (role: string, isSuperAdmin: boolean) => {
    if (isSuperAdmin) {
      return t('staff.superAdmin');
    }
    const roleKey = String(role || '').toLowerCase();
    return t(`roles.${roleKey}`, {
      defaultValue: String(role || '').replace('_', ' '),
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('staff.clinicianName'),
        cell: ({ row }: any) => {
          const first = row.original.first_name || '';
          const last = row.original.last_name || '';
          const initials =
            `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() ||
            (row.original.email || '?').charAt(0).toUpperCase();
          // Deterministic avatar color per user.
          const palette = [
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
            'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
            'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
            'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
          ];
          const seed = String(row.original.id || row.original.email || '')
            .split('')
            .reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0);
          return (
            <div className="flex items-center gap-3">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  palette[seed % palette.length]
                }`}
              >
                {initials}
              </span>
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900 dark:text-white">
                  {first} {last}
                </div>
                {!row.original.is_verified && (
                  <div className="text-xs text-amber-600 dark:text-amber-400">
                    {t('staff.pendingVerification', 'Pending verification')}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'acl_roles',
        header: t('staff.role', 'Role'),
        cell: ({ row }: any) => {
          const aclRoles: string[] = row.original.acl_roles || [];
          if (Boolean(row.original.is_tenant_owner)) {
            return <Badge text={t('staff.owner', 'Owner')} variant="danger" />;
          }
          if (aclRoles.length === 0) {
            return <Badge text={t('staff.noRole', 'No role')} variant="neutral" />;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {aclRoles.map((r: string) => (
                <Badge key={r} text={r} variant="info" />
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'acl_placements',
        header: t('staff.facility', 'Facility / Org'),
        cell: ({ row }: any) => {
          const placements: { name: string; type: string }[] =
            row.original.acl_placements || [];
          if (placements.length === 0) {
            return <span className="text-slate-400 text-xs">-</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {placements.map((p) => (
                <span
                  key={`${p.name}-${p.type}`}
                  className="inline-flex items-center gap-1.5"
                >
                  <span className="text-sm dark:text-slate-300">{p.name}</span>
                  <Badge text={p.type} variant="neutral" />
                </span>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'email',
        header: t('staff.hospitalEmail'),
        cell: ({ row }: any) => (
          <span className="dark:text-slate-300">{row.original.email}</span>
        ),
      },
      {
        accessorKey: 'is_active',
        header: t('staff.systemAccess'),
        cell: ({ row }: any) => (
          <Badge
            text={row.original.is_active ? t('Active') : t('Revoked')}
            variant={row.original.is_active ? 'success' : 'neutral'}
          />
        ),
      },
      {
        accessorKey: 'last_login',
        header: t('staff.lastLogin'),
        cell: ({ row }: any) => (
          <span className="text-slate-500 dark:text-slate-400 text-xs">
            {row.original.last_login
              ? new Date(row.original.last_login).toLocaleString()
              : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }: any) => (
          <div className="flex justify-end">
            <ActionMenu>
              <ActionMenuItem
                icon={Eye}
                label={t('staff.viewProfile')}
                onClick={() => navigate(`/profile/${row.original.id}`)}
              />
              <ActionMenuItem
                icon={Shield}
                label="Manage roles"
                onClick={() => setRolesUser(row.original)}
              />
              <ActionMenuItem
                icon={Key}
                label={t('staff.resetPassword')}
                disabled={sendPasswordReset.isPending}
                onClick={() =>
                  setChannelChoice({
                    action: 'reset',
                    userId: row.original.id,
                    phone: row.original.phone_number,
                  })
                }
              />

              {row.original.is_active ? (
                <ActionMenuItem
                  icon={UserX}
                  label={t('staff.suspendAccess')}
                  onClick={() =>
                    setConfirmAction({ type: 'suspend', user: row.original })
                  }
                />
              ) : (
                <ActionMenuItem
                  icon={UserCheck}
                  label={t('staff.restoreAccess')}
                  onClick={() =>
                    activateUser.mutate(row.original.id, {
                      onSuccess: () => users.refetch(),
                    })
                  }
                />
              )}
              {!row.original.is_verified && (
                <ActionMenuItem
                  icon={Send}
                  label={t('table.actions.resendVerification')}
                  onClick={() =>
                    setChannelChoice({
                      action: 'resend',
                      userId: row.original.id,
                      phone: row.original.phone_number,
                    })
                  }
                />
              )}
              <ActionMenuItem
                icon={Trash2}
                label={t('staff.remove')}
                className="text-red-600"
                onClick={() =>
                  setConfirmAction({ type: 'remove', user: row.original })
                }
              />
            </ActionMenu>
          </div>
        ),
      },
    ],
    [
      activateUser,
      deactivateUser,
      deleteUser,
      navigate,
      resendVerification,
      sendPasswordReset,
      t,
      users,
    ]
  );

  useEffect(() => {
    users.refetch();
  }, [filters, users.refetch]);

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      messageI18n.error('organization.uploadCsvRequired');
      event.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    importUsersCsv.mutate(formData, {
      onSuccess: (response: any) => {
        const data = response?.data;
        const created = data?.created ?? 0;
        const reactivated = data?.reactivated ?? 0;
        const skipped = data?.skipped ?? 0;
        let extraDetails = '';
        if (reactivated) {
          extraDetails += tToast('organization.staffImportReactivated', {
            count: reactivated,
          });
        }
        if (skipped) {
          extraDetails += tToast('organization.staffImportSkipped', {
            count: skipped,
          });
        }
        messageI18n.success('organization.staffImportSuccess', {
          created,
          pluralSuffix: created === 1 ? '' : 's',
          extraDetails,
        });
        users.refetch();
      },
      onError: (error: any) => {
        const errorResponse = error?.response?.data;
        const details = errorResponse?.details?.errors;
        if (Array.isArray(details) && details.length) {
          const firstError = details[0];
          message.error(
            tToast('organization.staffImportValidationError', {
              message:
                errorResponse?.message ||
                tToast('organization.importStaffFailed'),
              row: firstError.row,
              field: firstError.field,
              error: firstError.error,
            })
          );
          return;
        }
        if (typeof errorResponse?.message === 'string') {
          message.error(errorResponse.message);
          return;
        }
        message.error(error?.message || tToast('organization.importStaffFailed'));
      },
    });
    event.target.value = '';
  };

  const table = useReactTable({
    data: users.data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount:
      (users.data as any)?.total ??
      (users.data as any)?.total_count ??
      (users.data?.items?.length ?? 0),
    state: { pagination },
    onPaginationChange: setPagination,
  });

  return (
    <div className="animate-in fade-in">
      <div className="flex flex-col md:flex-row gap-3 mb-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-slate-200 dark:border-gray-700">
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 dark:text-gray-500"
          />
          <Input
            placeholder={t('staff.firstNamePlaceholder')}
            className="mt-1 pl-9 dark:bg-gray-900 dark:text-white"
            value={filterInputs.first_name}
            onChange={(e) => updateFilter('first_name', e.target.value)}
          />
        </div>
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 dark:text-gray-500"
          />
          <Input
            placeholder={t('staff.emailPlaceholder')}
            className="mt-1 pl-9 dark:bg-gray-900 dark:text-white"
            value={filterInputs.email}
            onChange={(e) => updateFilter('email', e.target.value)}
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" className="mt-1 shrink-0" onClick={clearFilters}>
            <X size={14} className="mr-1.5" />
            {t('staff.clearFilters', 'Clear')}
          </Button>
        )}

        {isAdmin && (
          <>
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
              <Plus size={16} className="mr-2" /> {t('staff.addStaff')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => importInputRef.current?.click()}
                disabled={importUsersCsv.isPending}
              >
                <Upload size={16} className="mr-2" />
                {importUsersCsv.isPending ? t('staff.importing') : t('staff.importCsv')}
              </Button>
              <Tooltip
                title={
                  <div className="whitespace-pre-line">
                    {t('staff.importCsvTooltip', {
                      roles: (aclRoles ?? [])
                        .map((r: any) => r.key)
                        .join(', '),
                    })}
                  </div>
                }
              >
                <div className="w-7 h-7 rounded-full border border-slate-200 dark:border-gray-700 flex items-center justify-center text-[11px] font-bold text-slate-500 dark:text-slate-300">
                  ?
                </div>
              </Tooltip>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImportCsv}
            />
          </>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-title-page-color-100 dark:text-white flex items-center gap-2">
              <Users className="text-blue-600" size={18} />
              {t('staff.tableTitle', 'Staff members')}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('staff.totalCount', '{{count}} total', {
                count:
                  (users.data as any)?.total ??
                  (users.data as any)?.total_count ??
                  users.data?.items?.length ??
                  0,
              })}
            </span>
          </div>
        </div>
        <Table
          table={table}
          isLoading={users.isLoading}
          isFetching={users.isFetching}
          columnsLength={7}
          onRowClick={(u: any) => navigate(`/profile/${u.id}`)}
          noDataMessage={
            hasActiveFilters
              ? t('staff.noFilterResults', 'No staff member matches your search.')
              : undefined
          }
        />
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        size="2xl"
      >
        <Modal.Header
          title={t('staff.addModalTitle')}
          onClose={() => setIsCreateModalOpen(false)}
        />
        <form
          onSubmit={(e: any) => {
            e.preventDefault();
            const fields = Object.fromEntries(new FormData(e.target));
            // Check the temporary password here rather than letting the server reject
            // it: this modal has no per-field error slots, so a rejection would arrive
            // as a toast after the admin already thought they were done.
            if (!sendSetupLink) {
              const passwordError = validatePasswordStrengthI18n(
                String(fields.password || '')
              );
              if (passwordError) {
                message.error(passwordError);
                return;
              }
            }
            createUser.mutate(fields, {
              onSuccess: () => {
                messageI18n.success('organization.staffCreated');
                setIsCreateModalOpen(false);
                users.refetch();
              },
            });
          }}
        >
          <Modal.Body>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                {t('staff.staffEmail')}
              </label>
              <Input
                name="email"
                type="email"
                required
                className="dark:bg-gray-900 dark:text-white"
              />
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                  {t('staff.deliveryMethod', 'Delivery method')}
                </label>
                <Select
                  name="verification_channel"
                  value={createChannel}
                  onChange={(e) =>
                    setCreateChannel(e.target.value as 'email' | 'whatsapp')
                  }
                  className="w-full rounded-lg border border-slate-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                  options={[
                    { value: 'email', label: t('staff.deliverEmail', 'Email') },
                    { value: 'whatsapp', label: t('staff.deliverWhatsapp', 'WhatsApp') },
                  ]}
                />
              </div>
              {createChannel === 'whatsapp' && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                    {t('staff.phoneWithCountryCode', 'Phone number (with country code)')}
                  </label>
                  <Input
                    name="phone_number"
                    type="tel"
                    required
                    placeholder="+212600000000"
                    className="dark:bg-gray-900 dark:text-white"
                  />
                </div>
              )}
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendSetupLink}
                  onChange={(e) => setSendSetupLink(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-600 dark:text-gray-300">
                  {t(
                    'staff.sendSetupLink',
                    'Send a setup link instead of a temporary password'
                  )}
                </span>
              </label>
              {!sendSetupLink && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                    {t('staff.temporaryPassword', 'Temporary password')}
                  </label>
                  <div className="flex items-stretch gap-2">
                    <Input
                      name="password"
                      type="text"
                      required
                      autoComplete="off"
                      placeholder={t(
                        'staff.temporaryPasswordPlaceholder',
                        'Type one or generate'
                      )}
                      value={temporaryPassword}
                      onChange={(e) => setTemporaryPassword(e.target.value)}
                      className="flex-1 min-w-0 dark:bg-gray-900 dark:text-white font-mono tracking-wide"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      className="shrink-0 gap-1.5 whitespace-nowrap"
                      onClick={() =>
                        setTemporaryPassword(generateTemporaryPassword())
                      }
                    >
                      <FiRefreshCw className="h-4 w-4" />
                      {t('staff.generatePassword', 'Generate')}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-gray-500">
                    {t(
                      'staff.temporaryPasswordHint',
                      'The password is sent over the channel above. They must set their own the first time they sign in, on the web or in the mobile app.'
                    )}
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                  {t('staff.aclRole', 'Access role')}
                </label>
                <Select
                  name="role_id"
                  required
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  placeholder={t('staff.selectRole', 'Select a role')}
                  className="w-full rounded-lg border border-slate-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                  options={(aclRoles || []).map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                  {t('staff.assignedFacility', 'Assigned to')}
                </label>
                <Select
                  key={selectedRoleId}
                  name="org_unit_id"
                  required
                  disabled={!selectedRoleId}
                  placeholder={
                    !selectedRoleId
                      ? t('staff.selectRoleFirst', 'Select a role first')
                      : allowedLevels && allowedLevels.length === 0
                      ? t('staff.roleNotAssignable', 'This role is not assignable to a node')
                      : t('staff.selectNode', 'Select a facility / org node')
                  }
                  className="w-full rounded-lg border border-slate-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 text-sm dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                  options={orgNodeOptions}
                />
                {!selectedRoleId && (
                  <p className="text-xs text-slate-400 dark:text-gray-500">
                    {t(
                      'staff.assignedFacilityHint',
                      'Choose an access role above first — it determines which org levels this person can be assigned to.'
                    )}
                  </p>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createUser.isPending}
            >
              {t('staff.createStaff')}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
      {rolesUser && (
        <UserRolesModal
          uid={rolesUser.id}
          userName={rolesUser.name || rolesUser.email}
          onClose={() => {
            setRolesUser(null);
            users.refetch();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(confirmAction)}
        title={
          confirmAction?.type === 'remove'
            ? t('staff.confirmRemoveTitle', 'Remove this staff member?')
            : t('staff.confirmSuspendTitle', 'Suspend access?')
        }
        message={
          confirmAction ? (
            <>
              <span className="font-medium">
                {[confirmAction.user.first_name, confirmAction.user.last_name]
                  .filter(Boolean)
                  .join(' ') || confirmAction.user.email}
              </span>{' '}
              {confirmAction.type === 'remove'
                ? t(
                    'staff.confirmRemoveBody',
                    'will be removed from the organization and lose all access.'
                  )
                : t(
                    'staff.confirmSuspendBody',
                    'will no longer be able to sign in. You can restore access at any time.'
                  )}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={
          confirmAction?.type === 'remove'
            ? t('staff.remove')
            : t('staff.suspendAccess')
        }
        cancelLabel={t('Cancel')}
        isPending={deleteUser.isPending || deactivateUser.isPending}
        onConfirm={runConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />

      {channelChoice && (
        <Modal isOpen onClose={() => setChannelChoice(null)} size="sm">
          <div className="p-6">
            <h3 className="text-lg font-bold text-title-page-color-100 dark:text-white">
              {t('staff.sendVia', 'Send via')}
            </h3>
            <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-400">
              {channelChoice.action === 'reset'
                ? t('staff.sendViaResetHint', 'How should the password reset be delivered?')
                : t('staff.sendViaVerifyHint', 'How should the verification be delivered?')}
            </p>
            <div className="mt-5 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                disabled={sendPasswordReset.isPending || resendVerification.isPending}
                onClick={() => runChannelChoice('email')}
              >
                {t('staff.viaEmail', 'Email')}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                disabled={
                  !channelChoice.phone ||
                  sendPasswordReset.isPending ||
                  resendVerification.isPending
                }
                onClick={() => runChannelChoice('whatsapp')}
              >
                {t('staff.viaWhatsapp', 'WhatsApp')}
              </Button>
            </div>
            {!channelChoice.phone && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                {t('staff.noPhoneForWhatsapp', 'No phone number on file - WhatsApp unavailable.')}
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

const PACSTab = ({ isAdmin }: any) => {
  const { data: devices, isLoading, refetch } = useGetDevicesWithConnections();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);

  const activeDeviceId =
    selectedDeviceId || (devices && devices.length > 0 ? devices[0].id : -1);

  const aeList = useGetApplicationEntitiesPageable({
    page: 0,
    size: 10,
    deviceId: activeDeviceId,
  });

  const startDevice = useStartDevice();
  const stopDevice = useStopDevice();
  const restartDevice = useRestartDevice();

  if (isLoading)
    return (
      <div className="p-20 text-center dark:text-white">
        <Spin tip="Loading Clinical Nodes..." />
      </div>
    );

  if (!devices || devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-20 bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-slate-200 dark:border-gray-700">
        <HardDrive
          size={48}
          className="text-slate-300 dark:text-slate-600 mb-4"
        />
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
          PACS Node Not Established
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-sm text-center">
          Link your ultrasound machine (GE Voluson, Philips Epiq) to start
          receiving exams.
        </p>
        <Button variant="primary" className="mt-6">
          <Plus size={16} className="mr-2" /> Link Clinical Machine
        </Button>
      </div>
    );
  }

  const device =
    devices.find((d: any) => d.id === activeDeviceId) || devices[0];
  const conn = device?.connections?.[0];

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
      <div className="flex justify-between items-center bg-white dark:bg-gray-800 p-4 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-3">
          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
            Imaging Node
          </p>
          <Select
            className="rounded-lg border border-slate-200 dark:border-gray-700 p-1.5 text-sm bg-slate-50 dark:bg-gray-900 font-bold text-blue-600 dark:text-blue-400 outline-none"
            value={String(activeDeviceId)}
            onChange={(e) => setSelectedDeviceId(Number(e.target.value))}
            options={devices.map((d: any) => ({
              value: String(d.id),
              label: d.deviceName,
            }))}
          />
        </div>
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            className="shadow-lg shadow-blue-100 dark:shadow-none"
          >
            <Plus size={16} className="mr-2" /> Link New Machine
          </Button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-8 space-y-8 shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex gap-4 items-center">
            <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-xl shadow-indigo-100 dark:shadow-none">
              <Monitor size={28} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                {device.deviceName}
              </h3>
              <Badge
                text={device.status}
                variant={device.status === 'ACTIVE' ? 'success' : 'neutral'}
              />
            </div>
          </div>
          <div className="flex gap-2">
            {device.status === 'INACTIVE' ? (
              <Button
                variant="success"
                size="sm"
                onClick={() =>
                  startDevice.mutate(device.id, { onSuccess: () => refetch() })
                }
                disabled={startDevice.isPending}
              >
                <Power size={14} className="mr-2" /> Start Node
              </Button>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  stopDevice.mutate(device.id, { onSuccess: () => refetch() })
                }
                disabled={stopDevice.isPending}
              >
                <PowerOff size={14} className="mr-2" /> Stop
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                restartDevice.mutate(device.id, { onSuccess: () => refetch() })
              }
              disabled={restartDevice.isPending}
            >
              <RotateCcw size={14} className="mr-2" /> Restart
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-slate-50 dark:bg-gray-900/40 p-6 rounded-2xl border border-slate-100 dark:border-gray-700">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              SCP Hostname
            </label>
            <p className="text-sm font-mono font-bold text-blue-600 dark:text-blue-400">
              {conn?.hostname || '0.0.0.0'}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              DICOM Port
            </label>
            <p className="text-sm font-mono font-bold text-slate-900 dark:text-white">
              {conn?.port || '2763'}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              TLS Security
            </label>
            <div className="flex items-center gap-2 mt-1">
              <Lock
                size={12}
                className={conn?.tlsEnabled ? 'text-green-500' : 'text-red-500'}
              />
              <span className="text-xs font-bold uppercase dark:text-gray-300">
                {conn?.tlsEnabled ? 'Encrypted' : 'Unsecured'}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 dark:text-blue-400 font-bold"
              onClick={() => {
                navigator.clipboard.writeText(conn?.hostname || '');
                messageI18n.success('organization.setupInfoCopied');
              }}
            >
              <Copy size={12} className="mr-1" /> Copy Setup
            </Button>
          </div>
        </div>

        <Card
          title="Logical Application Entities (AE Listing)"
          className="border-0 shadow-none bg-transparent p-0"
        >
          <div className="overflow-hidden border border-slate-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-gray-700/50 text-slate-400 dark:text-slate-500 uppercase text-[10px] font-bold tracking-widest">
                <tr>
                  <th className="p-4">AE Title</th>
                  <th className="p-4">DICOM Role</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {aeList.isLoading ? (
                  <tr>
                    <td colSpan={3} className="p-10 text-center">
                      <Spin size="small" />
                    </td>
                  </tr>
                ) : (
                  <>
                    {aeList?.data?.content && aeList.data.content.length > 0 ? (
                      aeList.data.content.map((ae: any) => (
                        <tr
                          key={ae.id}
                          className="border-t border-slate-100 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <td className="p-4 font-mono font-bold text-blue-600 dark:text-blue-400 underline underline-offset-4">
                            {ae.aeTitle}
                          </td>
                          <td className="p-4">
                            <Tag
                              color="blue"
                              className="dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                            >
                              {ae.aeRole}
                            </Tag>
                          </td>
                          <td className="p-4">
                            <Badge
                              text={ae.status}
                              variant={
                                ae.status === 'ACTIVE' ? 'success' : 'neutral'
                              }
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="p-10 text-center text-slate-400 dark:text-slate-500 italic"
                        >
                          No application entities registered for this machine.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

const SecurityTab = ({ orgData, isAdmin, refetch }: any) => {
  const { t } = useTranslation('Organization');
  const backend_url =
    (window as any).RUNTIME_ENV?.BACKEND_URL ??
    process.env.BACKEND_URL ??
    'http://localhost:8000/api/v1';

  const { data: samlConfig, isLoading: isSamlLoading } = useSamlSettings();
  const updateSamlSettings = useUpdateSamlSettings();
  const updateOrganization = useOrganizationUpdate();

  const [ssoUrl, setSsoUrl] = useState('');
  const [entityId, setEntityId] = useState('');
  const [certificate, setCertificate] = useState('');
  const idpMetadataInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (samlConfig) {
      setSsoUrl(samlConfig.acs_url || '');
      setEntityId(samlConfig.entity_id || '');
      setCertificate(samlConfig.certificate || '');
    }
  }, [samlConfig]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    messageI18n.success('organization.copiedToClipboard', { label });
  };

  const handleSaveSSO = () => {
    const payload = {
      acs_url: ssoUrl,
      entity_id: entityId,
      certificate: certificate,
    };

    updateSamlSettings.mutate(payload, {
      onSuccess: () => {
        messageI18n.success('organization.ssoUpdated');
      },
      onError: (err: any) => {
        messageI18n.error('organization.ssoUpdateFailed', {
          message: err.message,
        });
      },
    });
  };

  const spAcsUrl = `${backend_url}/saml/acs`;
  const spEntityIdUrl = `${backend_url}/saml/metadata`;
  const handleDownloadMetadata = async () => {
    try {
      const response = await fetch(spEntityIdUrl);
      if (!response.ok) {
        throw new Error('Failed to download metadata');
      }
      const xml = await response.blob();
      const url = window.URL.createObjectURL(xml);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'deepecho-sp-metadata.xml';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      message.error(
        err?.message || tToast('organization.metadataDownloadFailed')
      );
    }
  };

  const formatCertificatePem = (rawCert: string) => {
    const normalized = rawCert.replace(/[\r\n\s]/g, '');
    if (!normalized) {
      return '';
    }
    const lines = normalized.match(/.{1,64}/g) || [];
    return `-----BEGIN CERTIFICATE-----\n${lines.join('\n')}\n-----END CERTIFICATE-----`;
  };

  const extractElementsByLocalName = (doc: Document, localName: string) =>
    Array.from(doc.getElementsByTagName('*')).filter(
      (node) => node.localName === localName
    );

  const handleIdpMetadataUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const xmlText = await file.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, 'text/xml');
      const parseError = doc.getElementsByTagName('parsererror')[0];
      if (parseError) {
        throw new Error('Invalid XML metadata file.');
      }

      const entityDescriptor = extractElementsByLocalName(
        doc,
        'EntityDescriptor'
      )[0];
      const parsedEntityId = entityDescriptor?.getAttribute('entityID') || '';

      const ssoServices = extractElementsByLocalName(doc, 'SingleSignOnService');
      const preferredBindings = [
        'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect',
        'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
      ];
      let parsedSsoUrl = '';
      for (const binding of preferredBindings) {
        const match = ssoServices.find(
          (service) => service.getAttribute('Binding') === binding
        );
        if (match?.getAttribute('Location')) {
          parsedSsoUrl = match.getAttribute('Location') || '';
          break;
        }
      }
      if (!parsedSsoUrl && ssoServices[0]?.getAttribute('Location')) {
        parsedSsoUrl = ssoServices[0].getAttribute('Location') || '';
      }

      const keyDescriptors = extractElementsByLocalName(doc, 'KeyDescriptor');
      const signingKey =
        keyDescriptors.find((node) => node.getAttribute('use') === 'signing') ||
        keyDescriptors[0];
      const x509Nodes = signingKey
        ? Array.from(signingKey.getElementsByTagName('*')).filter(
            (node) => node.localName === 'X509Certificate'
          )
        : extractElementsByLocalName(doc, 'X509Certificate');
      const rawCert = x509Nodes[0]?.textContent?.trim() || '';

      if (!parsedEntityId || !parsedSsoUrl || !rawCert) {
        throw new Error('Missing required fields in IdP metadata.');
      }

      setEntityId(parsedEntityId);
      setSsoUrl(parsedSsoUrl);
      setCertificate(formatCertificatePem(rawCert));
      messageI18n.success('organization.idpImported');
    } catch (err: any) {
      message.error(err?.message || tToast('organization.idpParseFailed'));
    } finally {
      event.target.value = '';
    }
  };

  if (isSamlLoading)
    return (
      <div className="p-10 text-center dark:text-white">
        <Spin tip={t('security.loadingConfig')} />
      </div>
    );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in slide-in-from-bottom-6 duration-500">
      <div className="lg:col-span-2 space-y-6">
        <Card title={t('security.ssoGuideTitle')}>
          <div className="space-y-8">
            <div className="flex items-center gap-4 bg-blue-50 dark:bg-blue-900/20 p-5 rounded-2xl border border-blue-100 dark:border-blue-800">
              <Info
                className="text-blue-600 dark:text-blue-400 shrink-0"
                size={24}
              />
              <div>
                <h4 className="font-bold text-blue-900 dark:text-blue-200">
                  {t('security.googleSamlTitle')}
                </h4>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                  {t('security.googleSamlDescription')}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3 p-5 rounded-2xl border-2 border-transparent bg-slate-50 dark:bg-gray-800/50 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-slate-300 dark:bg-gray-600 text-white">
                    1
                  </div>
                  <h5 className="font-bold text-slate-900 dark:text-white uppercase tracking-tight text-xs">
                    {t('security.step1Title')}
                  </h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                      {t('security.acsUrl')}
                    </label>
                    <div className="flex gap-1">
                      <Input
                        readOnly
                        value={spAcsUrl}
                        className="bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-300 font-mono text-[10px]"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(spAcsUrl, t('security.acsUrl'))}
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                      {t('security.entityId')}
                    </label>
                    <div className="flex gap-1">
                      <Input
                        readOnly
                        value={spEntityIdUrl}
                        className="bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-300 font-mono text-[10px]"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(spEntityIdUrl, t('security.entityId'))}
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                    <div className="pt-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={handleDownloadMetadata}
                      >
                        <Download size={12} className="mr-2" />
                        {t('security.downloadMetadata')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 p-5 rounded-2xl border-2 border-blue-500/30 bg-blue-50/10 dark:bg-blue-900/10 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-blue-600 text-white">
                    2
                  </div>
                  <h5 className="font-bold text-slate-900 dark:text-white uppercase tracking-tight text-xs">
                    {t('security.step2Title')}
                  </h5>
                </div>
                <div className="space-y-4 pt-2">
                  <div className="flex flex-col gap-2 items-start">
                    <input
                      ref={idpMetadataInputRef}
                      type="file"
                      accept=".xml"
                      className="hidden"
                      onChange={handleIdpMetadataUpload}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => idpMetadataInputRef.current?.click()}
                    >
                      <Upload size={12} className="mr-2" />
                      {t('security.importIdpMetadata')}
                    </Button>
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      {t('security.importIdpHint')}
                    </p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1 block">
                      {t('security.idpSsoUrl')}
                    </label>
                    <Input
                      placeholder={t('security.idpSsoUrlPlaceholder')}
                      className="bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                      value={ssoUrl}
                      onChange={(e) => setSsoUrl(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1 block">
                      {t('security.idpEntityId')}
                    </label>
                    <Input
                      placeholder={t('security.idpEntityIdPlaceholder')}
                      className="bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                      value={entityId}
                      onChange={(e) => setEntityId(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase block">
                      {t('security.x509Certificate')}
                    </label>
                    <textarea
                      className="w-full h-32 p-3 text-[10px] font-mono border border-slate-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
                      placeholder={t('security.pemPlaceholder')}
                      value={certificate}
                      onChange={(e) => setCertificate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  {isAdmin
                    ? t(
                        'security.saveHint',
                        'Applies immediately to all sign-ins on this instance.'
                      )
                    : t('security.adminOnlyHint', 'Only administrators can change SSO settings.')}
                </p>
                <Button
                  variant="primary"
                  className="shrink-0"
                  disabled={!isAdmin || updateSamlSettings.isPending}
                  onClick={handleSaveSSO}
                >
                  {updateSamlSettings.isPending ? (
                    <>
                      <FiLoader className="animate-spin mr-2" /> {t('security.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={14} className="mr-2" />
                      {t('security.saveActivate')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card title={t('security.hardeningTitle')}>
          <div className="space-y-6">
            <div className="flex justify-between items-center gap-4 bg-slate-50 dark:bg-gray-800/50 p-4 rounded-xl border border-slate-100 dark:border-gray-700">
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {t('security.mandatory2fa')}
                </p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-bold tracking-widest">
                  {t('security.strictPolicy')}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <span
                  className={`text-xs font-semibold ${
                    orgData?.force_2fa_enabled
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-slate-400 dark:text-slate-500'
                  }`}
                >
                  {orgData?.force_2fa_enabled ? t('security.enforced') : t('security.off')}
                </span>
                <Switch
                  checked={Boolean(orgData?.force_2fa_enabled)}
                  disabled={!isAdmin || updateOrganization.isPending}
                  aria-label={t('security.mandatory2fa')}
                  onChange={(newVal) => {
                    updateOrganization.mutate(
                      { force_2fa_enabled: newVal },
                      {
                        onSuccess: () => {
                          messageI18n.success(
                            newVal
                              ? 'organization.twoFactorEnabled'
                              : 'organization.twoFactorDisabled'
                          );
                          refetch();
                        },
                      }
                    );
                  }}
                />
              </div>
            </div>

            <div className="p-4 border border-slate-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  {t('security.encryptionStatus')}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                  <span>{t('security.inTransit')}</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    TLS 1.3
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                  <span>{t('security.atRest')}</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    AES-256-GCM
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OrganizationPage;
